import { Check, MoreHorizontal, Pencil, Power, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { ShippingRate } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@nucleus-commerce/ui/components/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@nucleus-commerce/ui/components/table';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { RateKindBadge } from './rate-kind-badge';

export type RateAction = 'edit' | 'activate' | 'deactivate' | 'delete';

export interface RatesTableProps {
  rates: readonly ShippingRate[];
  /** Shows the row menu; false for read-only roles and closed stores. */
  editable: boolean;
  onAction: (action: RateAction, rate: ShippingRate) => void;
}

function Bands({ rate }: { rate: ShippingRate }) {
  const { t } = useTranslation('shipping');
  const format = useFormat();
  switch (rate.kind) {
    case 'weight': {
      const { minWeightGrams: min, maxWeightGrams: max } = rate;
      if (min !== null && max !== null) return t('rate.weightBand', { min, max });
      if (min !== null) return t('rate.weightFrom', { min });
      if (max !== null) return t('rate.weightUpTo', { max });
      return t('rate.anyOrder');
    }
    case 'price': {
      const { minSubtotal: min, maxSubtotal: max } = rate;
      if (min && max)
        return t('rate.subtotalBand', { min: format.money(min), max: format.money(max) });
      if (min) return t('rate.subtotalFrom', { min: format.money(min) });
      if (max) return t('rate.subtotalUpTo', { max: format.money(max) });
      return t('rate.anyOrder');
    }
    case 'free_over':
      return rate.minSubtotal
        ? t('rate.freeOver', { min: format.money(rate.minSubtotal) })
        : t('rate.anyOrder');
    case 'flat':
      return t('rate.anyOrder');
  }
}

function Delivery({ rate }: { rate: ShippingRate }) {
  const { t } = useTranslation('shipping');
  const { deliveryDaysMin: min, deliveryDaysMax: max } = rate;
  if (min !== null && max !== null) return t('rate.deliveryDays', { min, max });
  if (min !== null) return t('rate.deliveryDaysMin', { min });
  if (max !== null) return t('rate.deliveryDaysMax', { max });
  return <span className="text-muted-foreground">{t('rate.noDelivery')}</span>;
}

/** Rates of one zone in position order. */
export function RatesTable({ rates, editable, onAction }: RatesTableProps) {
  const { t } = useTranslation('shipping');
  const format = useFormat();

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="pl-6">{t('rate.columns.rate')}</TableHead>
          <TableHead>{t('rate.columns.kind')}</TableHead>
          <TableHead className="text-right">{t('rate.columns.price')}</TableHead>
          <TableHead>{t('rate.columns.bands')}</TableHead>
          <TableHead>{t('rate.columns.delivery')}</TableHead>
          <TableHead>{t('rate.columns.signature')}</TableHead>
          <TableHead>{t('rate.columns.active')}</TableHead>
          <TableHead className="w-12 pr-6">
            <span className="sr-only">{t('rate.columns.actions')}</span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rates.map((rate) => (
          <TableRow key={rate.id} className={cn(!rate.isActive && 'text-muted-foreground')}>
            <TableCell className="text-foreground pl-6 font-medium">{rate.name}</TableCell>
            <TableCell>
              <RateKindBadge kind={rate.kind} />
            </TableCell>
            <TableCell className="text-right tabular-nums">
              {rate.kind === 'free_over' || rate.price.amount === 0
                ? t('rate.free')
                : format.money(rate.price)}
            </TableCell>
            <TableCell className="text-sm">
              <Bands rate={rate} />
            </TableCell>
            <TableCell className="text-sm tabular-nums">
              <Delivery rate={rate} />
            </TableCell>
            <TableCell className="text-sm">
              {rate.requiresSignature ? (
                <span className="inline-flex items-center gap-1">
                  <Check className="size-3.5" aria-hidden />
                  {t('rate.signatureRequired')}
                </span>
              ) : (
                <span className="text-muted-foreground">{t('rate.signatureNo')}</span>
              )}
            </TableCell>
            <TableCell>
              <Badge
                variant="outline"
                className={cn(
                  rate.isActive
                    ? 'bg-success/15 text-success dark:bg-success/20 border-transparent'
                    : 'text-muted-foreground',
                )}
              >
                {rate.isActive ? t('rate.status.active') : t('rate.status.inactive')}
              </Badge>
            </TableCell>
            <TableCell className="pr-6">
              {editable ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label={t('rate.menu', { name: rate.name })}
                    >
                      <MoreHorizontal aria-hidden />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem onSelect={() => onAction('edit', rate)}>
                      <Pencil aria-hidden />
                      {t('rate.edit')}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onSelect={() => onAction(rate.isActive ? 'deactivate' : 'activate', rate)}
                    >
                      <Power aria-hidden />
                      {rate.isActive ? t('rate.deactivate') : t('rate.activate')}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      variant="destructive"
                      onSelect={() => onAction('delete', rate)}
                    >
                      <Trash2 aria-hidden />
                      {t('rate.delete')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : null}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
